import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapIcon from '@mui/icons-material/Map';
import RefreshIcon from '@mui/icons-material/Refresh';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MemoryIcon from '@mui/icons-material/Memory';
import { useNavigate } from 'react-router-dom';
import { gpsService } from '../api/api';
import { formatPortalDate } from '../utils/dateFormat';

function vehicleName(location) {
  return location.truck_name || location.vehicle_name || location.external_vehicle_id || location.device_id || 'Unmatched GPS Unit';
}

function secondaryLabel(location) {
  return [
    location.truck_license_plate || location.license_plate,
    location.vin ? `VIN ${location.vin}` : null,
    location.device_id ? `Device ${location.device_id}` : null,
  ].filter(Boolean).join(' • ') || 'No plate / device identifier';
}

function deviceName(device) {
  return device.display_name || device.displayName || device.vehicle_name || device.name || 'Unnamed Device';
}

function deviceId(device) {
  return device.device_id || device.deviceId || device.id || device.vuid || '';
}

function normalizedToken(value) {
  return String(value || '').trim().toLowerCase();
}

function mapUrl(location) {
  const lat = Number(location.latitude);
  const lng = Number(location.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

function embedMapUrl(location) {
  const lat = Number(location.latitude);
  const lng = Number(location.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
  const delta = 0.01;
  const bbox = [
    lng - delta,
    lat - delta,
    lng + delta,
    lat + delta,
  ].join(',');
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${encodeURIComponent(`${lat},${lng}`)}`;
}

function ageLabel(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function freshnessColor(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'default';
  const minutes = (Date.now() - date.getTime()) / 60000;
  if (minutes <= 15) return 'success';
  if (minutes <= 120) return 'warning';
  return 'default';
}

export default function LiveGps() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deviceError, setDeviceError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [query, setQuery] = useState('');
  const [deviceQuery, setDeviceQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      setError('');
      setDeviceError('');
      const [locationResult, deviceResult] = await Promise.allSettled([
        gpsService.getLatestLocations({ limit: 100 }),
        gpsService.getDevices(),
      ]);

      if (locationResult.status === 'fulfilled') {
        const rows = Array.isArray(locationResult.value.data) ? locationResult.value.data : [];
        setLocations(rows);
        if (!selectedId && rows[0]?.id) setSelectedId(rows[0].id);
      } else {
        setError(locationResult.reason?.response?.data?.error || locationResult.reason?.message || 'Failed to load live GPS locations.');
      }

      if (deviceResult.status === 'fulfilled') {
        setDevices(Array.isArray(deviceResult.value.data) ? deviceResult.value.data : []);
      } else {
        setDeviceError(deviceResult.reason?.response?.data?.error || deviceResult.reason?.message || 'Failed to load OneStepGPS devices.');
      }
      setLastRefresh(new Date());
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load live GPS locations.');
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = window.setInterval(() => load({ quiet: true }), 15_000);
    const onFocus = () => load({ quiet: true });
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredLocations = useMemo(() => {
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return locations;
    return locations.filter((location) => {
      const haystack = [
        vehicleName(location),
        secondaryLabel(location),
        location.address,
        location.external_vehicle_id,
        location.device_id,
      ].filter(Boolean).join(' ').toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    });
  }, [locations, query]);

  const selectedLocation = filteredLocations.find((location) => Number(location.id) === Number(selectedId)) || filteredLocations[0] || null;
  const selectedMapUrl = selectedLocation ? mapUrl(selectedLocation) : '';
  const selectedEmbedMapUrl = selectedLocation ? embedMapUrl(selectedLocation) : '';

  const locationByDevice = useMemo(() => {
    const map = new Map();
    locations.forEach((location) => {
      [
        location.device_id,
        location.external_vehicle_id,
        location.vehicle_name,
        location.truck_name,
      ].map(normalizedToken).filter(Boolean).forEach((key) => {
        if (!map.has(key)) map.set(key, location);
      });
    });
    return map;
  }, [locations]);

  const deviceLocation = (device) => {
    const keys = [
      deviceId(device),
      deviceName(device),
      device.display_name,
      device.displayName,
      device.name,
    ].map(normalizedToken).filter(Boolean);
    return keys.map((key) => locationByDevice.get(key)).find(Boolean) || null;
  };

  const filteredDevices = useMemo(() => {
    const tokens = deviceQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return devices;
    return devices.filter((device) => {
      const haystack = [
        deviceName(device),
        deviceId(device),
      ].filter(Boolean).join(' ').toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    });
  }, [devices, deviceQuery]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, minHeight: '100vh' }}>
      <Paper className="portal-page-header"
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 4,
          bgcolor: '#003b73',
          color: 'white',
          mb: 2,
          boxShadow: '0 16px 40px rgba(0,31,63,0.16)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: '#00e096', color: '#002b4f', boxShadow: '0 12px 24px rgba(0,224,150,0.24)' }}>
            <MyLocationIcon />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 950, lineHeight: 1.1, fontSize: { xs: '1.55rem', sm: '1.9rem' } }}>
              Live GPS View
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontWeight: 700 }}>
              OneStepGPS live truck locations • refreshes every 15 seconds
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Chip label={`${locations.length} unit${locations.length === 1 ? '' : 's'}`} sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: 'white', fontWeight: 900 }} />
          {lastRefresh && <Chip label={`Updated ${lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`} sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: 'white', fontWeight: 900 }} />}
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => load()} sx={{ bgcolor: '#00e096', color: '#002b4f', fontWeight: 900 }}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {deviceError && <Alert severity="warning" sx={{ mb: 2 }}>{deviceError}</Alert>}

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 4, mb: 2, boxShadow: '0 10px 30px rgba(16,42,67,0.08)', border: '1px solid #d9e7ef' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 40, height: 40, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: '#edf7ff', color: '#006fc9' }}>
              <MemoryIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 950, color: '#001f3f', lineHeight: 1.1 }}>
                OneStepGPS Devices
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                {devices.length} device{devices.length === 1 ? '' : 's'} from OneStepGPS
              </Typography>
            </Box>
          </Stack>
          <TextField
            size="small"
            value={deviceQuery}
            onChange={(event) => setDeviceQuery(event.target.value)}
            placeholder="Search devices..."
            sx={{ minWidth: { xs: '100%', md: 280 } }}
          />
        </Stack>

        <Grid container spacing={1} sx={{ mt: 1 }}>
          {filteredDevices.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">
                No OneStepGPS devices found yet. If auth is working, set `ONESTEPGPS_API_DEVICES_PATH=/v3/api/public/device-info?device_id=true&display_name=true`.
              </Alert>
            </Grid>
          ) : filteredDevices.map((device) => {
            const latest = deviceLocation(device);
            const deviceMapUrl = latest ? mapUrl(latest) : '';
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`${deviceName(device)}-${deviceId(device)}`}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderRadius: 3,
                    bgcolor: latest ? '#f1fff9' : '#fbfdff',
                    borderColor: latest ? '#a8e6c8' : '#d7e9f8',
                    height: '100%',
                  }}
                >
                  <Stack spacing={0.75}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 950, color: '#001f3f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {deviceName(device)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, overflowWrap: 'anywhere' }}>
                          {deviceId(device) ? `Device ${deviceId(device)}` : 'Device ID not returned'}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        color={latest ? freshnessColor(latest.event_time) : 'default'}
                        label={latest ? ageLabel(latest.event_time) : 'No GPS'}
                        sx={{ fontWeight: 900, flexShrink: 0 }}
                      />
                    </Stack>
                    {latest ? (
                      <>
                        <Typography variant="caption" sx={{ color: '#005b48', fontWeight: 800 }}>
                          {Number(latest.latitude).toFixed(5)}, {Number(latest.longitude).toFixed(5)}
                        </Typography>
                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                          {latest.speed_kph != null && <Chip size="small" label={`${Number(latest.speed_kph).toFixed(0)} km/h`} />}
                          {latest.ignition != null && <Chip size="small" color={latest.ignition ? 'success' : 'default'} label={latest.ignition ? 'On' : 'Off'} />}
                          {deviceMapUrl && <Button size="small" href={deviceMapUrl} target="_blank" rel="noreferrer" sx={{ fontWeight: 900, p: 0, minWidth: 'auto' }}>Map</Button>}
                        </Stack>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Waiting for live location from OneStepGPS.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {loading ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 1.5, fontWeight: 800 }}>Loading live locations...</Typography>
        </Paper>
      ) : locations.length === 0 ? (
        <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 4, border: '1px dashed #90a4ae', bgcolor: 'white' }}>
          <Stack spacing={1.25} alignItems="flex-start">
            <WarningAmberIcon sx={{ color: '#f57c00', fontSize: 38 }} />
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#001f3f' }}>No GPS locations received yet</Typography>
            <Typography color="text.secondary">
              The page is ready, but BioFleet has not stored any OneStepGPS messages yet.
            </Typography>
            <Alert severity="info" sx={{ width: '100%' }}>
              Check production env vars, run `npm run db:push`, restart `biofleet`, then watch `journalctl -u biofleet -f` for the RabbitMQ consumer message.
            </Alert>
          </Stack>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5} lg={4}>
            <Paper sx={{ p: 1.5, borderRadius: 4, boxShadow: '0 10px 30px rgba(16,42,67,0.08)' }}>
              <TextField
                fullWidth
                size="small"
                label="Search live trucks"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Truck, plate, VIN, device..."
                sx={{ mb: 1.5 }}
              />
              <Stack spacing={1} sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 280px)' }, overflow: 'auto', pr: { md: 0.5 } }}>
                {filteredLocations.map((location) => {
                  const selected = Number(selectedLocation?.id) === Number(location.id);
                  return (
                    <Card
                      key={location.id}
                      component="button"
                      onClick={() => setSelectedId(location.id)}
                      sx={{
                        textAlign: 'left',
                        borderRadius: 3,
                        border: selected ? '2px solid #00a878' : '1px solid #d9e7ef',
                        bgcolor: selected ? '#effdf8' : 'white',
                        cursor: 'pointer',
                        boxShadow: selected ? '0 8px 22px rgba(0,168,120,0.14)' : 'none',
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 950, color: '#001f3f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {vehicleName(location)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {secondaryLabel(location)}
                            </Typography>
                          </Box>
                          <Chip size="small" color={freshnessColor(location.event_time)} label={ageLabel(location.event_time)} sx={{ fontWeight: 900, flexShrink: 0 }} />
                        </Stack>
                        <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap' }}>
                          {location.speed_kph != null && <Chip size="small" label={`${Number(location.speed_kph).toFixed(0)} km/h`} />}
                          {location.ignition != null && <Chip size="small" color={location.ignition ? 'success' : 'default'} label={location.ignition ? 'Ignition on' : 'Ignition off'} />}
                          {location.odometer_km != null && <Chip size="small" label={`${Number(location.odometer_km).toLocaleString()} km`} />}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7} lg={8}>
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 4, boxShadow: '0 10px 30px rgba(16,42,67,0.08)', minHeight: 420 }}>
              {selectedLocation ? (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 950, color: '#001f3f' }}>
                        {vehicleName(selectedLocation)}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontWeight: 700 }}>
                        {secondaryLabel(selectedLocation)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                      {selectedLocation.truck_id && (
                        <Button variant="outlined" startIcon={<DirectionsCarIcon />} onClick={() => navigate(`/truck/${selectedLocation.truck_id}`)} sx={{ fontWeight: 900 }}>
                          Open Vehicle
                        </Button>
                      )}
                      {selectedMapUrl && (
                        <Button variant="contained" startIcon={<MapIcon />} href={selectedMapUrl} target="_blank" rel="noreferrer" sx={{ bgcolor: '#00897b', fontWeight: 900 }}>
                          Open Map
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} lg={4}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 4,
                          height: '100%',
                          minHeight: { xs: 180, lg: 360 },
                          bgcolor: '#e6f7f3',
                          borderColor: '#bde7dc',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          textAlign: 'center',
                        }}
                      >
                        <LocationOnIcon sx={{ fontSize: 58, color: '#00897b', mx: 'auto' }} />
                        <Typography variant="h6" sx={{ fontWeight: 950, color: '#001f3f', mt: 1 }}>
                          {Number(selectedLocation.latitude).toFixed(5)}, {Number(selectedLocation.longitude).toFixed(5)}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                          {selectedLocation.address || 'Address not provided by OneStepGPS'}
                        </Typography>
                        {selectedMapUrl && (
                          <Button sx={{ mt: 2, fontWeight: 900 }} variant="outlined" href={selectedMapUrl} target="_blank" rel="noreferrer">
                            View in Google Maps
                          </Button>
                        )}
                      </Paper>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                      <Paper
                        variant="outlined"
                        sx={{
                          borderRadius: 4,
                          overflow: 'hidden',
                          minHeight: { xs: 300, lg: 360 },
                          height: '100%',
                          borderColor: '#bde7dc',
                          bgcolor: '#eef8f6',
                          position: 'relative',
                        }}
                      >
                        {selectedEmbedMapUrl ? (
                          <Box
                            component="iframe"
                            title={`${vehicleName(selectedLocation)} map`}
                            src={selectedEmbedMapUrl}
                            sx={{
                              width: '100%',
                              height: { xs: 300, lg: 360 },
                              border: 0,
                              display: 'block',
                            }}
                          />
                        ) : (
                          <Box sx={{ minHeight: { xs: 300, lg: 360 }, display: 'grid', placeItems: 'center', p: 3, textAlign: 'center' }}>
                            <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>
                              Map unavailable until latitude and longitude arrive.
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>

                  <Grid container spacing={1.5}>
                    {[
                      ['Last GPS Time', formatPortalDate(selectedLocation.event_time, { includeTime: true })],
                      ['Speed', selectedLocation.speed_kph != null ? `${Number(selectedLocation.speed_kph).toFixed(0)} km/h` : 'N/A'],
                      ['Heading', selectedLocation.heading != null ? `${Number(selectedLocation.heading).toFixed(0)}°` : 'N/A'],
                      ['Odometer', selectedLocation.odometer_km != null ? `${Number(selectedLocation.odometer_km).toLocaleString()} km` : 'N/A'],
                      ['Truck Match', selectedLocation.truck_id ? `Truck #${selectedLocation.truck_id}` : 'Unmatched'],
                      ['External ID', selectedLocation.external_vehicle_id || 'N/A'],
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={6} md={4} key={label}>
                        <Paper variant="outlined" sx={{ p: 1.35, borderRadius: 3 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>{label}</Typography>
                          <Typography sx={{ fontWeight: 900, color: '#001f3f', overflowWrap: 'anywhere' }}>{value}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              ) : (
                <Typography color="text.secondary">No matching live locations for this search.</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
